import React, { forwardRef } from 'react';

import type { ButtonAsAnchorProps, ButtonAsButtonProps, ButtonProps } from './Button.types';
import * as styles from './Button.styles';

interface ButtonOverloads {
  (props: ButtonAsButtonProps & React.RefAttributes<HTMLButtonElement>): React.ReactElement | null;
  (props: ButtonAsAnchorProps & React.RefAttributes<HTMLAnchorElement>): React.ReactElement | null;
  displayName?: string;
}

function isAnchorProps(props: ButtonProps): props is ButtonAsAnchorProps {
  return (props as ButtonAsAnchorProps).href !== undefined;
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button({ children, compact = false, inverted = false, secondary, ...props }, ref) {
    const { as, ...anchorProps } = props as ButtonAsAnchorProps;
    const buttonStyles = [
      styles.root,
      styles.getVariantStyles({ inverted, secondary }),
      compact ? styles.compact : undefined,
    ];

    if (isAnchorProps({ children, compact, inverted, secondary, ...props } as ButtonProps)) {
      if (as && as !== 'a') {
        return React.createElement(
          as as React.ElementType,
          {
            ...anchorProps,
            css: buttonStyles,
            ref: ref as React.Ref<unknown>,
          },
          children,
        );
      }

      return (
        <a
          {...(anchorProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          css={buttonStyles}
          ref={ref as React.Ref<HTMLAnchorElement>}
        >
          {children}
        </a>
      );
    }

    return (
      <button
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        css={buttonStyles}
        ref={ref as React.Ref<HTMLButtonElement>}
      >
        {children}
      </button>
    );
  },
) as unknown as ButtonOverloads;

Button.displayName = 'Button';
